import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { formatMoneyValue } from '../../../../../app/utils/formatMoneyValue'
import { Withdraw } from '../../../../../app/entities/Withdraw'
import Status from '@/view/components/Status'
import { StatusEnum } from '@/app/utils/status'
import { MoneySend, Refresh } from 'iconsax-react'
import { WithdrawService } from '@/app/services/WithdrawService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ErrorWithdrawModal } from './ErrorWithdrawModal'
import { Spinner } from '@/view/components/ui/spinner'
import { useAuth } from '@/app/hooks/useAuth'
import { toast } from '@/view/components/ui/use-toast'

export const ColumnDefinitionSellerCommission = () => {
  const columnHelper = createColumnHelper<Withdraw>()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  const [showModal, setShowModal] = useState(false)
  const [selectedWithdrawId, setSelectedWithdrawId] = useState<string | null>(
    null,
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<string | null>(null)
  const [isLoadingProof, setIsLoadingProof] = useState<string | null>(null)

  const { mutateAsync } = useMutation({
    mutationFn: (id: string) => WithdrawService.getWithdrawById(id),
    onSuccess(data) {
      if (data.errorOnUpdate) {
        setSelectedWithdrawId(data._id)
        setErrorMessage(data.errorOnUpdate)
        setShowModal(true)
      } else {
        queryClient.invalidateQueries({ queryKey: ['withdraws'] })
      }
    },
  })

  const refreshWithdraw = async (id: string) => {
    try {
      setIsLoading(id)
      await mutateAsync(id)
    } catch (e) {
      console.log(e)
    } finally {
      setIsLoading(null)
    }
  }

  const handleProofClick = async (withdraw: Withdraw) => {
    const pixTransferKey = withdraw.pix_transfer_key
    if (!pixTransferKey || withdraw.status !== 'PAID' || isLoadingProof) {
      return
    }

    try {
      setIsLoadingProof(withdraw._id)
      const pdfBlob = await WithdrawService.getWithdrawProof({ pixTransferKey })
      const url = URL.createObjectURL(pdfBlob)
      window.open(url, '_blank')
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 1000)
    } catch (error) {
      console.error('Erro ao abrir comprovante:', error)
      toast({
        description: 'Erro ao buscar o comprovante',
        variant: 'destructive',
      })
    } finally {
      setIsLoadingProof(null)
    }
  }

  const columnsSellerCommissions = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        id: 'data',
        header: () => (
          <div className="flex items-center justify-start pl-2">Data</div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-start  pl-0 text-xs xs:pl-4 xs:text-sm">
            {format(row.original.createdAt, 'dd/MM/yyyy, HH:mm')}
          </div>
        ),
      }),
      columnHelper.accessor('amount', {
        id: 'value',
        header: () => (
          <div className="flex flex-col items-start">
            <div>Valor</div>
          </div>
        ),
        cell: ({ row }) => (
          <div className=" flex flex-col items-start ">
            <div className="flex items-center gap-2 text-xs xs:text-sm">
              {formatMoneyValue(row.original.amount)}
              {row.original.pix_fee && <MoneySend size="20" color="#EB5757" />}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: () => (
          <div className="flex items-center justify-end pr-2">Status</div>
        ),
        cell: ({ row }) => (
          <>
            {isAdmin ? (
              <div
                className="flex items-center justify-end pr-0 xs:pr-4"
                onClick={(e) => {
                  e.stopPropagation()
                  if (isLoading === null) {
                    refreshWithdraw(row.original._id)
                  }
                }}
              >
                <div className="group flex items-center justify-center gap-2">
                  {isLoading === row.original._id ? (
                    <Spinner size="small" />
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg group-hover:border ">
                      <Refresh size={14} className="hidden group-hover:block" />
                    </div>
                  )}
                  <Status status={row.original.status as StatusEnum} />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end pr-0 xs:pr-4">
                <Status status={row.original.status as StatusEnum} />
              </div>
            )}
          </>
        ),
      }),
    ],
    [columnHelper, handleProofClick, isLoadingProof],
  )

  return {
    columnsSellerCommissions,
    handleProofClick,
    ErrorWithdrawModal: (
      <ErrorWithdrawModal
        open={showModal}
        onClose={() => setShowModal(false)}
        withdrawId={selectedWithdrawId}
        errorMessage={errorMessage}
      />
    ),
  }
}
